/**
 * Origem do pedido a partir da query string do link do cardápio — vai em `orders.external_source`.
 *
 * O cardápio antigo (AngularJS, `multipedidos-sistema/webview`) gravava a query string crua no
 * pedido: `pedir.delivery/loja?fbclid=…` virava `source = "fbclid=…"`. Integradoras usavam isso para
 * atribuir pedido a campanha da Meta/Google. Quando ele saiu do ar (07/07/2023) o pedir.delivery
 * passou a receber esse tráfego, mas só lia `?src=` — `fbclid`, `gclid` e `utm_*` caíam no chão.
 *
 * Regra:
 *  - `?src=` presente → vale ele, como sempre (o Wabot depende de `external_source == 'wabot'`).
 *  - senão → a query string crua, SEM os parâmetros internos do app (token, mesa, carrinho…).
 *  - sem nada que sobre → null.
 *
 * Vai em `external_source`, não em `source`: `source` é o canal (`web`/`pos`/`ifood`…) e o backend
 * decide coisas por ele.
 */

/** Parâmetros que o próprio app usa — nunca são origem, e alguns são credenciais. */
export const PARAMETROS_INTERNOS: ReadonlySet<string> = new Set([
    'src', 'campaign',
    'u',                    // recuperação de carrinho
    'wpu',                  // token do telefone do WhatsApp
    't',                    // mesa
    'repeat-order-uuid',
    'token',                // redefinição de senha
    'pd_preview',           // prévia do layout novo
    // retorno do Mercado Pago / pagamento
    'preference_id', 'collection_id', 'collection_status', 'external_reference', 'payment_id',
    'payment_type', 'merchant_order_id', 'merchant_account_id', 'processing_mode', 'site_id',
    'status', 'cid', 'oid',
]);

/** Tamanho da coluna `orders.external_source` (varchar 255). */
export const EXTERNAL_SOURCE_MAX = 255;

export function externalSourceFromSearch(search: string | null | undefined): string | null {
    const params = new URLSearchParams(search || '');

    const src = (params.get('src') || '').trim();
    if (src) return src.slice(0, EXTERNAL_SOURCE_MAX);

    const resto = new URLSearchParams();
    params.forEach((valor, chave) => {
        if (!PARAMETROS_INTERNOS.has(chave)) resto.append(chave, valor);
    });

    const crua = resto.toString();
    return crua ? crua.slice(0, EXTERNAL_SOURCE_MAX) : null;
}

const chave = (restaurantURL: string) => `externalSource.${restaurantURL}`;

/**
 * Captura a origem do link de entrada e a mantém na aba.
 *
 * A query some da barra durante a navegação: o `reloadRestaurant` recarrega a loja pela URL limpa e
 * o reload de recuperação de chunk troca a barra pela URL canônica. Sem guardar, o pedido saía sem
 * origem mesmo o cliente tendo entrado pelo anúncio. Link novo COM origem substitui a guardada;
 * link sem origem não apaga.
 */
export function captureExternalSource(search: string, restaurantURL: string, storage: Storage | null): string | null {
    const daUrl = externalSourceFromSearch(search);

    try {
        if (daUrl) {
            storage?.setItem(chave(restaurantURL), daUrl);
            return daUrl;
        }
        return storage?.getItem(chave(restaurantURL)) || null;
    } catch (e) {
        return daUrl;
    }
}
